import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Event } from '@api/models/event/event';
import { BehaviorSubject } from 'rxjs';
import { EventsHttpService } from '@api/http/events/events-http.service';
import { LivestreamsHttpService } from '@api/http/livestreams/livestreams-http.service';

export interface IMatchPoll {
  match: Event;
  teamId: string;
  updates: BehaviorSubject<IMatchPollUpdates>;
  active: MatchPollUpdateType[];
  subscribers: number;
}

export interface IMatchPollUpdates {
  type: MatchPollUpdateType;
  data: any;
}

export enum MatchPollUpdateType {
  Status = 'status',
  Tabs = 'tabs',
  Timeline = 'timeline',
  Overlay = 'overlay',
}

@Injectable({
  providedIn: 'root',
})
export class MatchPollingService {
  private interval: any;
  private readonly intervalMs = 1000;
  private pollCount = 0;
  private polls = new Map<string, IMatchPoll>();
  private lastVisibilityChange: number;

  constructor(
    private eventHttpService: EventsHttpService,
    private livestreamsHttp: LivestreamsHttpService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState == 'visible') {
        if (
          Date.now() - this.lastVisibilityChange > 1000 * 60 &&
          this.interval
        ) {
          clearInterval(this.interval);
          this.interval = setInterval(
            this.triggerAll.bind(this),
            this.intervalMs
          );
          this.triggerAll();
        }
      } else {
        this.lastVisibilityChange = Date.now();
      }
    });
  }

  public add(
    match: Event,
    teamId: string,
    types: MatchPollUpdateType[]
  ): IMatchPoll | null {
    if (!isPlatformBrowser(this.platformId) || !this.canPoll(match, types)) {
      return null;
    }
    const poll = this.updatePoll(match, teamId, types);
    if (!this.interval) {
      this.interval = setInterval(this.triggerAll.bind(this), this.intervalMs);
    }
    return poll;
  }

  private updatePoll(
    match: Event,
    teamId: string,
    types?: MatchPollUpdateType[]
  ): IMatchPoll {
    let poll = this.polls.get(match._id);
    const isNew = !poll;
    if (!poll) {
      poll = {
        match,
        teamId,
        updates: new BehaviorSubject<IMatchPollUpdates>(null),
        active: [],
        subscribers: 0,
      };
      this.polls.set(match._id, poll);
    }
    if (!types) {
      types = Object.values(MatchPollUpdateType);
    }
    poll.active.push(...types);
    poll.subscribers++;
    // Trigger initial polls immediately
    if (isNew && this.pollCount === 0) {
      this.triggerPoll(poll);
    }
    return poll;
  }

  public remove(match: Event, types?: MatchPollUpdateType[]): void {
    if (!match || !isPlatformBrowser(this.platformId)) {
      return;
    }
    const poll = this.polls.get(match._id);
    if (!poll) {
      return;
    }
    if (!types) {
      types = Object.values(MatchPollUpdateType) as MatchPollUpdateType[];
    }
    // Deactivate polling for given types
    types.forEach((type) => {
      const idx = poll.active.findIndex((active) => active === type);
      if (idx > -1) {
        poll.active.splice(idx, 1);
      }
    });
    poll.subscribers--;
  }

  private canPoll(match: Event, types: MatchPollUpdateType[]): boolean {
    if (!match) {
      return false;
    }
    // Overlay is always polled
    if (types.includes(MatchPollUpdateType.Overlay)) {
      return true;
    }
    if (match.isPreGame()) {
      return match.isToday();
    }
    return !match.isEnded();
  }

  private triggerAll(): void {
    this.pollCount++;
    this.polls.forEach((poll) => {
      // Every 5s
      if (this.pollCount % 5 === 0) {
        this.pollOverlay(poll);
      }
      // Every 20s
      if (this.pollCount % 20 === 0) {
        this.pollStatus(poll);
        this.pollTimeline(poll);
      }
      // Every 60s
      if (this.pollCount % 60 === 0) {
        this.pollTabs(poll);
      }
    });
  }

  private triggerPoll(poll: IMatchPoll): void {
    if (poll.subscribers === 0) {
      return;
    }
    this.pollStatus(poll);
    this.pollTabs(poll);
    this.pollTimeline(poll);
  }

  private async pollStatus(poll: IMatchPoll): Promise<void> {
    if (!poll.active.includes(MatchPollUpdateType.Status)) {
      return;
    }
    if (!poll.match.willStartSoon() || poll.match.isPostGameOrCancelled()) {
      return;
    }
    console.log('[Ticker Poll] Updating status for: ', poll.match._id);
    const response = await this.eventHttpService.getStatus(poll.match._id);
    poll.updates.next({
      type: MatchPollUpdateType.Status,
      data: response.data,
    });
  }

  private async pollTabs(poll: IMatchPoll): Promise<void> {
    if (!poll.active.includes(MatchPollUpdateType.Tabs)) {
      return;
    }
    console.log('[Ticker Poll] Updating tabs for: ', poll.match._id);
    const response = await this.eventHttpService.getEventTabs(
      poll.match._id,
      !!poll.match.sportradar?.sr_match_id
    );
    poll.updates.next({ type: MatchPollUpdateType.Tabs, data: response.data });
  }

  private async pollTimeline(poll: IMatchPoll): Promise<void> {
    if (!poll.active.includes(MatchPollUpdateType.Timeline)) {
      return;
    }
    if (!poll.match.willStartSoon() || poll.match.isEnded()) {
      return;
    }
    console.log('[Ticker Poll] Updating ticker for: ', poll.match._id);
    const response = await this.eventHttpService.getTimeline(
      poll.match._id,
      poll.teamId
    );
    poll.updates.next({ type: MatchPollUpdateType.Timeline, data: response });
  }

  private async pollOverlay(poll: IMatchPoll): Promise<void> {
    if (!poll.active.includes(MatchPollUpdateType.Overlay)) {
      return;
    }
    console.log('[Ticker Poll] Update overlay: ', poll.match._id);
    const response = await this.livestreamsHttp.getMatchOverlayData(
      poll.match._id
    );
    poll.updates.next({ type: MatchPollUpdateType.Overlay, data: response });
  }
}
